<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';

const props = defineProps({
  enabled: { type: Boolean, default: false },
  file: { type: Object, default: null },
  processing: { type: Boolean, default: false },
});

const emit = defineEmits(['update:enabled', 'update:file']);
const { t } = useI18n();
const fileInput = ref(null);
const benefits = computed(() => [
  t('SALES.CREATE.ONUS.BENEFIT_1'),
  t('SALES.CREATE.ONUS.BENEFIT_2'),
  t('SALES.CREATE.ONUS.BENEFIT_3'),
]);

const updateEnabled = value => {
  emit('update:enabled', value);
  if (!value && props.file) emit('update:file', null);
};

const chooseFile = () => fileInput.value?.click();

const selectFile = event => {
  emit('update:file', event.target.files?.[0] || null);
  event.target.value = '';
};
</script>

<template>
  <section
    data-testid="sales-property-onus-upload"
    class="grid gap-4 rounded-lais border border-lais-border bg-white p-5 sm:p-6"
  >
    <div>
      <h2 class="text-lg font-bold text-lais-text">
        {{ t('SALES.CREATE.ONUS.TITLE') }}
      </h2>
      <p class="mt-2 max-w-2xl text-sm leading-6 text-lais-text-muted">
        {{ t('SALES.CREATE.ONUS.DESCRIPTION') }}
      </p>
    </div>

    <ul v-if="!enabled" class="grid gap-2 text-sm text-lais-text">
      <li
        v-for="benefit in benefits"
        :key="benefit"
        class="flex items-start gap-2"
      >
        <span
          class="i-lucide-check mt-0.5 size-4 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
        <span>{{ benefit }}</span>
      </li>
    </ul>

    <LaisToggle
      :model-value="enabled"
      :disabled="processing"
      :label="t('SALES.CREATE.ONUS.TOGGLE')"
      @update:model-value="updateEnabled"
    />

    <div
      v-if="enabled"
      class="rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft p-4"
    >
      <input
        ref="fileInput"
        data-testid="sales-property-onus-file"
        class="hidden"
        type="file"
        accept="application/pdf,.pdf"
        @change="selectFile"
      />

      <div
        v-if="file"
        class="flex flex-wrap items-center justify-between gap-3"
      >
        <div class="flex min-w-0 items-center gap-2">
          <span class="i-lucide-file-check size-5 text-lais-primary" />
          <span class="truncate text-sm font-semibold text-lais-text">
            {{ file.name }}
          </span>
        </div>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-x"
          :disabled="processing"
          @click="emit('update:file', null)"
        >
          {{ t('SALES.CREATE.ONUS.REMOVE_FILE') }}
        </LaisPrimaryButton>
      </div>

      <LaisPrimaryButton
        v-else
        variant="ghost"
        icon="i-lucide-upload"
        :disabled="processing"
        @click="chooseFile"
      >
        {{ t('SALES.CREATE.ONUS.SELECT_FILE') }}
      </LaisPrimaryButton>
    </div>
  </section>
</template>
